import React, { useEffect, useMemo, useState } from 'react';
import { settingsAPI, instagramAPI } from '../../services/api';

const InstagramMediaCarousel = ({ media, postId, personId, onMediaDelete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  if (!media || media.length === 0) return null;

  const currentMedia = media[currentIndex];

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % media.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + media.length) % media.length);
  };

  const handleDeleteCurrentMedia = async () => {
    if (!window.confirm('Delete this media item?')) return;

    try {
      await instagramAPI.deleteMedia(personId, postId, currentMedia.filename);
      if (media.length === 1) {
        onMediaDelete(postId, null);
      } else {
        if (currentIndex >= media.length - 1) {
          setCurrentIndex(Math.max(0, currentIndex - 1));
        }
        onMediaDelete(postId, currentMedia.filename);
      }
    } catch (e) {
      alert('Failed to delete media');
    }
  };

  return (
    <>
      <div
        className="relative w-full bg-tech-surface rounded-lg overflow-hidden group cursor-pointer"
        style={{ height: '300px' }}
        onClick={() => setIsExpanded(true)}
      >
        {currentMedia.type === 'video' ? (
          <video
            key={currentMedia.url}
            src={currentMedia.url}
            controls
            className="w-full h-full object-contain"
          />
        ) : (
          <img
            key={currentMedia.url}
            src={currentMedia.url}
            alt="Instagram content"
            className="w-full h-full object-contain"
          />
        )}

        {media.length > 1 && (
          <button
            onClick={handleDeleteCurrentMedia}
            className="absolute top-2 left-2 p-2 bg-tech-danger text-white rounded opacity-0 group-hover:opacity-100 transition z-10"
            title="Delete this item"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {media.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1">
              {media.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full transition ${
                    idx === currentIndex ? 'bg-white' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {isExpanded && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50"
          onClick={() => setIsExpanded(false)}
        >
          <div className="relative w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            {currentMedia.type === 'video' ? (
              <video
                key={currentMedia.url}
                src={currentMedia.url}
                controls
                style={{ maxWidth: '90vw', maxHeight: '90vh' }}
                className="object-contain"
              />
            ) : (
              <img
                key={currentMedia.url}
                src={currentMedia.url}
                alt="Instagram content"
                style={{ maxWidth: '90vw', maxHeight: '90vh' }}
                className="object-contain"
              />
            )}

            <button
              onClick={() => setIsExpanded(false)}
              className="absolute top-2 right-2 p-2 bg-tech-danger text-white rounded-lg hover:bg-tech-danger/90 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {media.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevSlide(); }}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 transition"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextSlide(); }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 transition"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {media.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                      className={`w-3 h-3 rounded-full transition ${
                        idx === currentIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const InstagramTab = ({ person, updateField }) => {
  const accounts = useMemo(() => person?.socialMedia?.instagram || [], [person]);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [newUrl, setNewUrl] = useState('');
  const [newUrlAccount, setNewUrlAccount] = useState('');
  const [cookies, setCookies] = useState('');
  const [savingCookies, setSavingCookies] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setSelectedAccount('all');
    setNewUrlAccount(accounts[0]?.username || '');
  }, [accounts]);

  const posts = person.instagramPosts || [];
  const filteredPosts = (selectedAccount === 'all'
    ? posts
    : posts.filter(p => p.account === selectedAccount))
    .slice()
    .sort((a, b) => new Date(b.addedAt || 0) - new Date(a.addedAt || 0));

  useEffect(() => {
    (async () => {
      try {
        const res = await settingsAPI.getInstagramCookies();
        setCookies(res.data.cookies || '');
      } catch {}
    })();
  }, []);

  const saveCookies = async () => {
    setSavingCookies(true);
    try {
      await settingsAPI.setInstagramCookies(cookies);
    } finally {
      setSavingCookies(false);
    }
  };

  const addPost = async () => {
    if (!newUrl) return;
    setDownloading(true);
    try {
      const res = await instagramAPI.download(person.id, newUrl, newUrlAccount || selectedAccount || '');
      const entry = res.data.post;
      updateField('instagramPosts', [...posts, entry]);
      setNewUrl('');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const removePost = async (id) => {
    try {
      await instagramAPI.deletePost(person.id, id);
      updateField('instagramPosts', posts.filter(p => p.id !== id));
    } catch (e) {
      alert('Failed to delete post');
    }
  };

  const handleMediaDelete = (postId, filename) => {
    if (filename === null) {
      updateField('instagramPosts', posts.filter(p => p.id !== postId));
    } else {
      const updatedPosts = posts.map(p => {
        if (p.id === postId) {
          return {
            ...p,
            media: p.media.filter(m => m.filename !== filename)
          };
        }
        return p;
      });
      updateField('instagramPosts', updatedPosts);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-4 p-4">
      {/* Account Selection & Download Bar */}
      <div className="tech-card p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-xs font-mono text-tech-textMuted">ACCOUNT:</label>
            <select
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              className="tech-input py-1.5"
            >
              <option value="all">ALL</option>
              {accounts.map(acc => (
                <option key={acc.id} value={acc.username}>{acc.username}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 flex items-center gap-2 min-w-[300px]">
            <input
              type="url"
              placeholder="Paste Instagram URL (post, reel, or story)..."
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              className="tech-input py-1.5"
            />
            <select
              value={newUrlAccount}
              onChange={(e) => setNewUrlAccount(e.target.value)}
              className="tech-input py-1.5"
            >
              <option value="">-- SELECT ACCOUNT --</option>
              {accounts.map(acc => (
                <option key={acc.id} value={acc.username}>{acc.username}</option>
              ))}
            </select>
          </div>
          <button 
            onClick={addPost} 
            disabled={downloading || !newUrl} 
            className="tech-btn-primary disabled:opacity-50"
          >
            {downloading ? 'DOWNLOADING...' : 'DOWNLOAD'}
          </button>
          <Settings cookies={cookies} setCookies={setCookies} saving={savingCookies} onSave={saveCookies} />
        </div>
      </div>

      {/* Posts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPosts.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <p className="text-tech-textDim text-sm">NO INSTAGRAM POSTS YET</p>
          </div>
        ) : (
          filteredPosts.map(post => (
            <div key={post.id} className="relative group">
              <div className="tech-card overflow-hidden">
                <InstagramMediaCarousel
                  media={post.media}
                  postId={post.id}
                  personId={person.id}
                  onMediaDelete={handleMediaDelete}
                />

                <div className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    {post.type === 'story' ? (
                      <span className="text-xs text-tech-warning font-mono">STORY</span>
                    ) : (
                      <span className="text-xs text-tech-accent font-mono">POST</span>
                    )}
                    {post.owner && (
                      <span className="text-xs text-tech-textMuted">@{post.owner}</span>
                    )}
                    {post.media.length > 1 && (
                      <span className="text-xs text-tech-textDim">
                        {post.media.length} items
                      </span>
                    )}
                  </div>
                  {post.caption && (
                    <p className="text-sm text-tech-textMuted line-clamp-2">{post.caption}</p>
                  )}
                </div>
              </div>

              <button
                onClick={() => removePost(post.id)}
                className="absolute top-2 right-2 p-2 bg-tech-danger text-white rounded opacity-0 group-hover:opacity-100 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const Settings = ({ cookies, setCookies, saving, onSave }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="tech-btn-secondary py-1.5" title="Cookie Settings">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>
      {open && (
        <div className="fixed inset-0 bg-tech-bg/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setOpen(false)}>
          <div className="tech-card max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-mono font-semibold text-tech-text mb-4">INSTAGRAM COOKIES</h3>
            <p className="text-tech-textMuted text-sm mb-3">sessionid=...; csrftoken=...; ds_user_id=...</p>
            <p className="text-tech-textMuted text-xs mb-3">
              Required for downloading stories. For posts and reels, cookies may not be necessary.
            </p>
            <textarea 
              rows={5} 
              value={cookies} 
              onChange={(e)=>setCookies(e.target.value)} 
              className="tech-input font-mono text-sm mb-4"
              placeholder="sessionid=...; csrftoken=...; ds_user_id=..."
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setOpen(false)} className="tech-btn-secondary">CLOSE</button>
              <button onClick={onSave} disabled={saving} className="tech-btn-primary disabled:opacity-50">
                {saving ? 'SAVING...' : 'SAVE'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstagramTab;
